import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import './Tooltip.css';

const Tooltip = ({ content, children, position = 'top', metricId = null }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef(null);
  const timeoutRef = useRef(null);

  const updatePosition = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      
      let top, left;
      switch (position) {
        case 'top':
          top = rect.top - 80;
          left = rect.left + rect.width / 2;
          break;
        case 'bottom':
          top = rect.bottom + 20;
          left = rect.left + rect.width / 2;
          break;
        case 'left':
          top = rect.top + rect.height / 2;
          left = rect.left - 20;
          break;
        case 'right':
          top = rect.top + rect.height / 2;
          left = rect.right + 20;
          break;
        default:
          top = rect.top - 80;
          left = rect.left + rect.width / 2;
      }
      
      setTooltipPosition({ top, left });
    }
  }, [position]);

  useEffect(() => {
    if (isVisible) {
      updatePosition();
      window.addEventListener('scroll', updatePosition);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isVisible, position, updatePosition]);

  const tooltipRef = useRef(null);

  const tooltipElement = isVisible && (
    <div 
      ref={tooltipRef}
      onMouseEnter={() => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      }}
      onMouseLeave={() => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
          setIsVisible(false);
        }, 100);
      }}
      style={{
        position: 'fixed',
        top: tooltipPosition.top,
        left: tooltipPosition.left,
        zIndex: 2147483647,
        pointerEvents: 'auto',
        background: '#1f2937',
        color: 'white',
        padding: '12px 16px',
        borderRadius: '8px',
        fontSize: '14px',
        lineHeight: '1.4',
        maxWidth: '280px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        transform: 'translateX(-50%)'
      }}
    >
      <div style={{ marginBottom: metricId ? '8px' : '0' }}>
        {content}
      </div>
      {metricId && (
        <Link 
          to={`/metrics-dictionary#${metricId}`}
          style={{
            color: '#60a5fa',
            textDecoration: 'underline',
            fontSize: '12px',
            display: 'block',
            marginTop: '8px',
            fontWeight: 500
          }}
          onClick={(e) => {
            e.stopPropagation();
            setIsVisible(false);
          }}
        >
          Learn more →
        </Link>
      )}
    </div>
  );

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, 300);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 100);
  };

  return (
    <>
      <div 
        ref={containerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>
      {tooltipElement && ReactDOM.createPortal(tooltipElement, document.body)}
    </>
  );
};

export default Tooltip;